////// Lib. //////
//////////////////

import { gsap } from "gsap";
import Splide from "@splidejs/splide";
import { refreshLazyLoad } from "../../../../app/modules/LazyLoad.js";

const MOBILE_MEDIA_QUERY = "(max-width: 767px), (max-height: 767px) and (pointer: coarse)";

// Image stream class //
//////////////////

export default class ProjectImageStream {
  // Setup //
  //////////////////

  constructor({ zoomStep = 4 } = {}) {
    this._zoomStep = zoomStep;
    // On mobile the horizontal auto-scroll is replaced by a classic Splide slideshow.
    this._isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    this._splide = null;
    this._tween = null;
    this._zoomTween = null;
    this._zoomed = false;
    this._baseHeight = 0;
    this._userPaused = false;
    this._activePanel = null;
    this._activeImagesEl = null;
    this._activeTrack = null;
    this._activeFirstSet = null;
    this._trackSetWidth = 0;
    this._scrollPosition = 0;
    this._scrollVelocity = 0;
    this._scrollTicker = null;
    this._lastTouchX = 0;
    this._onWheel = null;
    this._onTouchStart = null;
    this._onTouchMove = null;
  }

  // Methods //
  //////////////////

  async open(item) {
    const panel = this._panelFor(item);
    const imagesEl = panel?.querySelector(".project-viewer-v2__item-images");
    if (!panel || !imagesEl) return;

    this._renderImages(item, imagesEl);

    panel.classList.remove("hidden");
    panel.setAttribute("aria-hidden", "false");
    item.setAttribute("aria-expanded", "true");

    gsap.set(panel, { height: 0 });
    this._activePanel = panel;
    this._activeImagesEl = imagesEl;

    // Mount the slideshow before measuring, so the images container reports its
    // real (slide-driven) height for the open animation.
    if (this._isMobile) this._mountSlider(imagesEl);

    const targetHeight = imagesEl.offsetHeight;
    await this._animate(panel, { height: targetHeight, duration: 0.5, ease: "power2.inOut" });
    gsap.set(panel, { height: "auto" });

    // Mobile is a static slideshow — no auto-scroll loop.
    if (this._isMobile) return;

    this._startAutoScroll(imagesEl);
  }

  close(item, { immediate = false } = {}) {
    const panel = this._panelFor(item);
    if (!panel || panel.classList.contains("hidden")) return Promise.resolve();

    this._tween?.kill();
    this._zoomTween?.kill();
    this._zoomTween = null;
    this._stopAutoScroll();
    item.setAttribute("aria-expanded", "false");

    if (immediate) {
      this._hide(panel);
      return Promise.resolve();
    }

    gsap.set(panel, { height: panel.offsetHeight });
    return this._animate(panel, { height: 0, duration: 0.5, ease: "power2.inOut", onComplete: () => this._hide(panel) });
  }

  destroy() {
    this._tween?.kill();
    this._zoomTween?.kill();
    this._stopAutoScroll();
    if (this._activePanel) this._hide(this._activePanel);
  }

  // Toggles the open panel's image stream between its base height and one fixed zoom level
  // (base + zoomStep * --unit-spacing). The panel itself stays height:auto (set at the end of
  // open()), so it just follows the images container's height for free — only imagesEl is tweened.
  // Returns the new zoomed state, or null if there's nothing to toggle.
  toggleZoom(panel) {
    if (!panel || panel !== this._activePanel || this._zoomTween) return null;

    const imagesEl = panel.querySelector(".project-viewer-v2__item-images");
    const unit = this._unitSpacingPx();
    if (!imagesEl || !unit) return null;

    const oldHeight = imagesEl.offsetHeight;
    if (!this._zoomed) this._baseHeight = oldHeight;

    const willZoom = !this._zoomed;
    const newHeight = willZoom ? this._baseHeight + this._zoomStep * unit : this._baseHeight;
    const startScrollPosition = this._scrollPosition;
    const track = this._activeTrack;

    // Pause the loop while the images resize — every image scales by the same factor as
    // the height (h-full/w-auto), so the transform is kept in lockstep with that same
    // factor every frame below instead of freezing it, which is what caused the jump.
    this._pauseAutoScroll();

    const heightProxy = { value: oldHeight };
    this._zoomTween = gsap.to(heightProxy, {
      value: newHeight,
      duration: 0.5,
      ease: "power2.inOut",
      onUpdate: () => {
        imagesEl.style.height = `${heightProxy.value}px`;

        const ratio = heightProxy.value / oldHeight;
        this._scrollPosition = startScrollPosition * ratio;
        if (track) track.style.transform = `translateX(${-this._scrollPosition}px)`;
      },
      onComplete: () => {
        this._zoomTween = null;
        this._zoomed = willZoom;

        // Back at base size: drop the inline override so the CSS height takes over again.
        if (!willZoom) imagesEl.style.height = "";

        this._trackSetWidth = this._activeFirstSet?.offsetWidth || this._trackSetWidth;
        this._resumeAutoScroll();
      },
    });

    return willZoom;
  }

  _unitSpacingPx() {
    const value = getComputedStyle(document.documentElement).getPropertyValue("--unit-spacing");
    return parseFloat(value) || 0;
  }

  // Explicit user pause/resume of the auto-scroll. Returns the new playing state, or
  // null if there's no active panel to toggle. Kept separate from the zoom pause/resume
  // (see _pauseAutoScroll/_resumeAutoScroll) so a zoom doesn't override a user pause.
  togglePlayback(panel) {
    if (!panel || panel !== this._activePanel) return null;

    this._userPaused = !this._userPaused;

    if (this._userPaused) {
      this._pauseAutoScroll();
    } else if (!this._zoomTween) {
      this._resumeAutoScroll();
    }

    return !this._userPaused;
  }

  _panelFor(item) {
    return item?.nextElementSibling?.classList.contains("project-viewer-v2__item-panel") ? item.nextElementSibling : null;
  }

  _animate(el, options) {
    return new Promise((resolve) => {
      this._tween = gsap.to(el, {
        ...options,
        onComplete: () => {
          options.onComplete?.();
          this._tween = null;
          resolve();
        },
      });
    });
  }

  _hide(panel) {
    panel.classList.add("hidden");
    panel.setAttribute("aria-hidden", "true");
    gsap.set(panel, { clearProps: "height" });

    // Mobile: tear the slideshow down and drop the rendered markup, so the next
    // open() rebuilds it cleanly (only one panel is ever open at a time).
    if (this._isMobile) {
      this._splide?.destroy(true);
      this._splide = null;

      const imagesEl = panel.querySelector(".project-viewer-v2__item-images");
      if (imagesEl) {
        imagesEl.replaceChildren();
        imagesEl.dataset.rendered = "";
        imagesEl.style.height = "";
      }

      if (this._activePanel === panel) {
        this._activePanel = null;
        this._activeImagesEl = null;
      }
      return;
    }

    // Reset the loop while hidden, so the next open() doesn't visibly jump back to the start.
    if (this._activeTrack) {
      this._scrollPosition = 0;
      this._activeTrack.style.transform = "translateX(0px)";
    }

    // Drop any toggleZoom() override so the next open() starts back at the base CSS height.
    const imagesEl = panel.querySelector(".project-viewer-v2__item-images");
    if (imagesEl) imagesEl.style.height = "";
    this._zoomed = false;

    if (this._activePanel === panel) {
      this._activePanel = null;
      this._activeImagesEl = null;
      this._activeTrack = null;
      this._activeFirstSet = null;
      this._trackSetWidth = 0;
    }
  }

  // Renders two copies of the image set ("work-stream"-style), so the loop can
  // wrap around seamlessly from the end of set A into the start of set B.
  _renderImages(item, imagesEl) {
    if (imagesEl.dataset.rendered === "true") return;

    let images = [];
    try {
      images = JSON.parse(item.dataset.images || "[]");
    } catch {
      images = [];
    }

    if (!images.length) return;

    // Mobile: one slide per image, no duplicated set (the desktop loop needs two).
    if (this._isMobile) {
      this._renderSlides(images, imagesEl);
      imagesEl.dataset.rendered = "true";
      refreshLazyLoad();
      return;
    }

    const buildSet = () =>
      images.map((image) => {
        const element = document.createElement("img");
        // The placeholder shares the original's aspect ratio, and --ar pins it
        // explicitly, so h-full/w-auto resolves to the final width before the
        // real source loads — the loop's offsetWidth math depends on that.
        element.src = image.placeholder || image.src;
        element.dataset.src = image.src;
        if (image.srcset) element.dataset.srcset = image.srcset;
        element.alt = image.alt || "";
        element.decoding = "async";
        element.className = "lazy block h-full w-auto shrink-0 object-cover aspect-(--ar) border-r-[1.5px] border-kl-blue";
        if (image.ratio) element.style.setProperty("--ar", image.ratio);
        return element;
      });

    const track = document.createElement("div");
    track.className = "project-viewer-v2__item-track flex flex-row h-full";

    const setA = document.createElement("div");
    setA.className = "project-viewer-v2__item-set flex flex-row gap-x-0 h-full shrink-0";
    setA.append(...buildSet());

    const setB = document.createElement("div");
    setB.className = "project-viewer-v2__item-set flex flex-row gap-x-0 h-full shrink-0";
    setB.setAttribute("aria-hidden", "true");
    setB.append(...buildSet());

    track.append(setA, setB);
    imagesEl.replaceChildren(track);

    imagesEl.dataset.rendered = "true";
    refreshLazyLoad();
  }

  // Builds the Splide markup for the mobile slideshow — a single slide per image.
  // Mirrors the staff-images slideshow structure so the two share their styling.
  _renderSlides(images, imagesEl) {
    const root = document.createElement("div");
    root.className = "project-viewer-v2__item-slider splide";

    const track = document.createElement("div");
    track.className = "splide__track";

    const list = document.createElement("ul");
    list.className = "splide__list list-none m-0 p-0";

    images.forEach((image) => {
      const slide = document.createElement("li");
      slide.className = "splide__slide";

      const element = document.createElement("img");
      element.src = image.placeholder || image.src;
      element.dataset.src = image.src;
      if (image.srcset) element.dataset.srcset = image.srcset;
      element.alt = image.alt || "";
      element.decoding = "async";
      element.className = "lazy block size-full object-cover";
      if (image.ratio) element.style.setProperty("--ar", image.ratio);

      slide.append(element);
      list.append(slide);
    });

    track.append(list);
    root.append(track);
    imagesEl.replaceChildren(root);
  }

  // Mounts a classic slideshow on the rendered markup — same options as the
  // staff-images mobile slider.
  _mountSlider(imagesEl) {
    const root = imagesEl.querySelector(".project-viewer-v2__item-slider");
    if (!root) return;

    this._splide = new Splide(root, {
      type: "slide",
      rewind: true,
      perPage: 1,
      arrows: false,
      pagination: true,
      gap: 0,
      drag: true,
      speed: 400,
      autoHeight: false,
      reducedMotion: {
        speed: 0,
        rewindSpeed: 0,
      },
    });

    this._splide.mount();
  }

  // Endless horizontal loop, ported from WorkStream's modulo-based ticker —
  // translateX() on the track wraps at one set's width instead of clamping scrollLeft.
  //////////////////////////////////////////////////////////////////////////

  _startAutoScroll(imagesEl) {
    if (this._scrollTicker) return;

    const track = imagesEl.querySelector(".project-viewer-v2__item-track");
    const firstSet = imagesEl.querySelector(".project-viewer-v2__item-set");
    if (!track || !firstSet) return;

    const setWidth = firstSet.offsetWidth;
    if (!setWidth) return;

    this._activeTrack = track;
    this._activeFirstSet = firstSet;
    this._trackSetWidth = setWidth;
    this._userPaused = false;

    // _scrollPosition was already reset to 0 by _hide() while the panel was invisible,
    // so just sync the transform to it instead of forcing it here (which would jump visibly).
    track.style.transform = `translateX(${-this._scrollPosition}px)`;

    this._onWheel = (e) => {
      e.preventDefault();
      let delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      if (e.deltaMode === 1) delta *= 20;
      if (e.deltaMode === 2) delta *= 400;
      this._scrollVelocity = Math.max(-300, Math.min(300, this._scrollVelocity + delta * 0.5));
    };

    this._onTouchStart = (e) => {
      this._lastTouchX = e.touches[0].clientX;
    };

    this._onTouchMove = (e) => {
      const delta = this._lastTouchX - e.touches[0].clientX;
      this._lastTouchX = e.touches[0].clientX;
      this._scrollVelocity = Math.max(-300, Math.min(300, this._scrollVelocity + delta * 1.2));
    };

    imagesEl.addEventListener("wheel", this._onWheel, { passive: false });
    imagesEl.addEventListener("touchstart", this._onTouchStart, { passive: true });
    imagesEl.addEventListener("touchmove", this._onTouchMove, { passive: true });

    this._scrollTicker = (time, deltaTime) => {
      const dt = Math.min(deltaTime, 100);
      const advance = (60 * dt) / 1000 + this._scrollVelocity;

      this._scrollPosition += advance;
      this._scrollPosition = ((this._scrollPosition % this._trackSetWidth) + this._trackSetWidth) % this._trackSetWidth;

      track.style.transform = `translateX(${-this._scrollPosition}px)`;

      this._scrollVelocity *= 0.9;
      if (Math.abs(this._scrollVelocity) < 0.05) this._scrollVelocity = 0;
    };

    gsap.ticker.add(this._scrollTicker);
  }

  _pauseAutoScroll() {
    if (this._scrollTicker) gsap.ticker.remove(this._scrollTicker);
  }

  _resumeAutoScroll() {
    if (this._userPaused) return;
    if (this._scrollTicker) gsap.ticker.add(this._scrollTicker);
  }

  _stopAutoScroll() {
    if (this._scrollTicker) {
      gsap.ticker.remove(this._scrollTicker);
      this._scrollTicker = null;
    }

    const imagesEl = this._activeImagesEl;
    if (imagesEl) {
      if (this._onWheel) imagesEl.removeEventListener("wheel", this._onWheel);
      if (this._onTouchStart) imagesEl.removeEventListener("touchstart", this._onTouchStart);
      if (this._onTouchMove) imagesEl.removeEventListener("touchmove", this._onTouchMove);
    }

    // _activeTrack/_scrollPosition are intentionally left as-is here (this runs immediately
    // on close(), while the panel is still visible) — _hide() resets them once actually hidden.
    this._scrollVelocity = 0;
    this._onWheel = null;
    this._onTouchStart = null;
    this._onTouchMove = null;
  }
}
